import React, { useState, useEffect, useRef } from 'react';
import { useTable } from 'react-table';
import { useDrag, useDrop } from 'react-dnd';
import update from 'immutability-helper';

import './DragAndDropTable.scss';

const DND_ITEM_TYPE = 'row';

function Table(props) {
  const { columns, data, getRowId, getDataRow, getSubRow, updateData, showMove, hideRequiredFields } = props;
  const [records, setRecords] = useState(data);

  useEffect(() => {
    setRecords(props.data);
  }, [props.data]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    data: records,
    columns,
    getRowId
  });

  const moveRow = (dragIndex, hoverIndex) => {
    const dragRecord = records[dragIndex];
    setRecords(
      update(records, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord]
        ]
      })
    );
  };

  const rowsToRender = rows.map((row, index) => {
    return (
      prepareRow(row) || (
        <Row
          index={index}
          row={row}
          moveRow={moveRow}
          getDataRow={getDataRow}
          getSubRow={getSubRow}
          updateData={() => updateData(records)}
          getRowId={getRowId}
          showMove={showMove}
          hideRequiredFields={hideRequiredFields}
          {...row.getRowProps()}
        />
      )
    );
  });

  return (
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              <th></th>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>{rowsToRender || []}</tbody>
      </table>
  );
}

function Row(props) {
  const { getDataRow, getRowId, getSubRow, index, moveRow, row, showMove, updateData, hideRequiredFields } = props;
  const dropRef = useRef(null);
  const dragRef = useRef(null);

  const [, drop] = useDrop({
    accept: DND_ITEM_TYPE,
    hover(item, monitor) {
      if (!dropRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveRow(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    drop(item, monitor) {
      updateData();
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_ITEM_TYPE,
    item: { type: DND_ITEM_TYPE, index },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0 : 1;
  let display = {};
  if (hideRequiredFields && row.original?.mandatory_field) {
    display = { display: 'none' };
  }

  useEffect(() => {
    if (dropRef.current) {
        preview(dropRef.current);
    }
  }, [preview]);

  //preview(drop(dropRef));
  drop(dropRef);
  drag(dragRef);

  return (
    <>
      <tr ref={dropRef} style={{ opacity, ...display }} key={`row-${getRowId(row)}`}>
        {showMove ? (
          <td ref={dragRef} className="dnd-table__drag-container">
            <div className="dnd-table__drag-dots" />
            <div className="dnd-table__drag-dots" />
          </td>
        ) : (
          <td />
        )}
        {getDataRow(row)}
      </tr>
      {getSubRow && (
        <div className="aggregate-type__sub-row" key={`sub-row-${getRowId(row)}`}>
          {getSubRow(row)}
        </div>
      )}
    </>
  );
}

export default function DragAndDropTable(props) {
  return (
    <div className="dnd-table">
      <Table
        columns={props.columns}
        data={[...props.data]}
        getRowId={props.getRowId}
        getDataRow={props.getDataRow}
        getSubRow={props.getSubRow}
        updateData={props.updateData}
        showMove={props.showMove}
        hideRequiredFields={props.hideRequiredFields}
      />
    </div>
  );
}
